import React from "react";
import { Box, useTheme } from "@mui/material";
import {
  AddRounded,
  Check as CheckIcon,
  TuneRounded,
} from "@mui/icons-material";
import AdaptivePopover from "../../common/AdaptivePopover";
import MenuRow from "../../common/MenuRow";
import { Watchlist } from "./hooks/useWatchlists";
import { hapticSelection } from "../../../lib/haptics";
import { useTouchLayout } from "../../../hooks/useTouchLayout";

interface WatchlistSwitcherProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  watchlists: Watchlist[];
  selectedWatchlistId: string | null;
  onSelect: (watchlistId: string) => void;
  /** Touch only — opens the create-watchlist modal. */
  onCreate?: () => void;
  /** Touch only — opens the manage modal for the active watchlist. */
  onManage?: () => void;
  /** Set when the watchlist limit is reached; disables the create row. */
  createDisabledReason?: string;
  /** Name of the watchlist the manage row acts on. */
  manageLabel?: string;
  /** Shows the create and manage rows on pointer too, where the menu is the only place they live. */
  actionsInMenu?: boolean;
  /** An "All watchlists" row above the lists (the Earnings hub). */
  allOption?: { selected: boolean; onSelect: () => void };
}

/**
 * Picks which watchlist a tab shows, and on TOUCH also owns the full CRUD.
 *
 * On pointer, create and manage are icon buttons beside the tab strip and this
 * is a pure picker. On touch those buttons had nowhere to live: floating above
 * the strip they read as unattached to anything (Ben, 2026-07-31: "they just
 * look out of place"). The sheet is where the user already goes to think about
 * watchlists, so the actions belong in it.
 *
 * ACTIONS ABOVE THE LIST, not below. The list grows with every watchlist the
 * user makes, so anything under it drifts below the fold exactly as the user
 * accumulates enough lists to need managing them. Above, they hold a fixed
 * position no matter how long the list gets — and a destructive action never
 * sits at the end of a scroll where a thumb lands by momentum. Delete itself
 * stays one level deeper, inside the manage modal, behind its own confirm.
 */
const WatchlistSwitcher: React.FC<WatchlistSwitcherProps> = ({
  open,
  anchorEl,
  onClose,
  watchlists,
  selectedWatchlistId,
  onSelect,
  onCreate,
  onManage,
  createDisabledReason,
  manageLabel,
  actionsInMenu = false,
  allOption,
}) => {
  const theme = useTheme();
  const isTouch = useTouchLayout();

  const handleSelect = (watchlistId: string) => {
    hapticSelection();
    onSelect(watchlistId);
    onClose();
  };

  // Close first: both open a modal, and leaving the sheet up behind it stacks
  // two layers the user has to dismiss in order.
  const handleAction = (action?: () => void) => {
    if (!action) return;
    hapticSelection();
    onClose();
    action();
  };

  const showActions =
    (isTouch || actionsInMenu) &&
    (onCreate !== undefined || onManage !== undefined);

  const listSelected = (watchlistId: string) =>
    allOption?.selected !== true && watchlistId === selectedWatchlistId;

  return (
    <AdaptivePopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      title="Your watchlists"
    >
      {showActions && (
        <>
          <Box role="group">
            {onCreate && (
              <MenuRow
                icon={<AddRounded fontSize="small" />}
                label="New watchlist"
                disabled={createDisabledReason !== undefined}
                disabledReason={createDisabledReason}
                onClick={() => handleAction(onCreate)}
              />
            )}
            {onManage && (
              <MenuRow
                icon={<TuneRounded fontSize="small" />}
                label={
                  manageLabel ? `Manage ${manageLabel}` : "Manage watchlist"
                }
                onClick={() => handleAction(onManage)}
              />
            )}
          </Box>
          <Box
            sx={{
              my: 1,
              borderTop: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(0, 0, 0, 0.08)"
              }`,
            }}
          />
        </>
      )}

      <Box role="menu">
        {allOption && (
          <MenuRow
            label="All watchlists"
            selected={allOption.selected}
            trailing={
              allOption.selected && (
                <CheckIcon fontSize="small" sx={{ color: "#22c55e" }} />
              )
            }
            onClick={() => {
              hapticSelection();
              allOption.onSelect();
              onClose();
            }}
          />
        )}
        {watchlists.map((watchlist) => (
          <MenuRow
            key={watchlist.id}
            label={watchlist.name}
            detail={`${watchlist.holdings_count} ${watchlist.holdings_count === 1 ? "stock" : "stocks"}`}
            selected={listSelected(watchlist.id)}
            trailing={
              listSelected(watchlist.id) ? (
                <CheckIcon fontSize="small" sx={{ color: "#22c55e" }} />
              ) : null
            }
            onClick={() => handleSelect(watchlist.id)}
          />
        ))}
      </Box>
    </AdaptivePopover>
  );
};

export default WatchlistSwitcher;
